import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CheckCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { ReactComponent as LapturaMark } from '../assets/brand/laptura-mark.svg';
import {
  DRIVE_SECONDS,
  DRIVE_START_ASPECT,
  LapturaDriveIn,
} from './common/LapturaDriveIn';

export const DROP_SECONDS = 0.55;
export const BLEND_SECONDS = 0.45;

let savedMarkRect = null;

export function saveLoginMarkRect(el) {
  if (!el) return;
  const r = el.getBoundingClientRect();
  savedMarkRect = { left: r.left, top: r.top, width: r.width, height: r.height };
}

export function peekLoginMarkRect() {
  return savedMarkRect;
}

export function clearLoginMarkRect() {
  savedMarkRect = null;
}

export function welcomeBarSeconds(hasMark) {
  return (hasMark ? DROP_SECONDS + BLEND_SECONDS : 0) + DRIVE_SECONDS;
}

export function welcomeCenterRect() {
  const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
  const h = 4 * rem;
  const w = h * DRIVE_START_ASPECT;
  // Same stack as the overlay: h-16 logo, then mt-10 + h-1 bar.
  const groupH = h + 2.5 * rem + 0.25 * rem;
  return {
    left: (window.innerWidth - w) / 2,
    top: (window.innerHeight - groupH) / 2,
    width: w,
    height: h,
  };
}

export function WelcomeOverlay({ username, onComplete, signedIn = true }) {
  const still = useReducedMotion();
  const fromRef = useRef(still ? null : peekLoginMarkRect());
  const from = fromRef.current;
  const [phase, setPhase] = useState(() => (from ? 'drop' : 'logo'));
  const driving = phase !== 'drop';
  const showingMark = phase === 'drop' || phase === 'blend';
  const to = from ? welcomeCenterRect() : null;
  const markTransform = from && to
    ? {
        x: to.left - from.left,
        y: to.top - from.top,
        scaleX: to.width / from.width,
        scaleY: to.height / from.height,
      }
    : null;
  const barSeconds = welcomeBarSeconds(Boolean(from));

  useEffect(() => {
    if (!driving) return undefined;
    const timer = setTimeout(() => setPhase('welcome'), DRIVE_SECONDS * 1000 + 150);
    return () => clearTimeout(timer);
  }, [driving]);

  useEffect(() => {
    if (phase !== 'welcome' || !signedIn) return undefined;
    const timer2 = setTimeout(() => setPhase('exit'), 1250);
    const timer3 = setTimeout(() => {
      clearLoginMarkRect();
      onComplete();
    }, 1850);
    return () => {
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [phase, signedIn, onComplete]);

  return (
    <AnimatePresence>
      {phase !== 'exit' && (
        <motion.div
          className="fixed inset-0 z-[100] bg-lap-bg flex items-center justify-center overflow-hidden"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6, ease: 'easeInOut' }}
        >
          <motion.div
            className="absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
            style={{
              background: 'radial-gradient(ellipse at center, rgba(37, 99, 235, 0.15) 0%, transparent 70%)',
            }}
          />

          <div className="absolute inset-0 overflow-hidden">
            {[...Array(5)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute h-[2px] bg-lap-accent"
                style={{ top: `${20 + i * 15}%`, left: 0, right: 0 }}
                initial={{ x: '-100%', opacity: 0 }}
                animate={{ x: '200%', opacity: [0, 1, 1, 0] }}
                transition={{
                  duration: 2.2,
                  delay: 0.4 + 0.12 * i,
                  ease: 'easeInOut',
                }}
              />
            ))}
          </div>

          <div className="relative z-10 flex flex-col items-center">
            <div className="relative">
              <motion.div
                className="absolute left-1/2 top-1/2 h-20 w-20 -translate-x-1/2 -translate-y-1/2 rounded-full bg-lap-accent/30 blur-2xl"
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.3, 0.6, 0.3],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              />
              {driving ? (
                <motion.div
                  data-testid="welcome-drive-in"
                  initial={{ opacity: from ? 0 : 1 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: from ? BLEND_SECONDS : 0, ease: 'easeOut' }}
                >
                  <LapturaDriveIn className="h-16 w-auto text-lap-text relative z-10" />
                </motion.div>
              ) : (
                <div
                  data-testid="welcome-l-slot"
                  className="h-16"
                  style={{ width: `calc(4rem * ${DRIVE_START_ASPECT})` }}
                />
              )}
            </div>

            <motion.div
              className="mt-10 w-48 h-1 bg-lap-raised rounded-full overflow-hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <motion.div
                className="h-full bg-lap-accent rounded-full"
                initial={{ width: '0%' }}
                animate={{ width: '100%' }}
                transition={{ duration: barSeconds, ease: 'easeInOut' }}
              />
            </motion.div>

            <AnimatePresence>
              {phase === 'welcome' && signedIn && (
                <div
                  data-testid="welcome-copy"
                  className="pointer-events-none absolute top-full left-1/2 z-10 mt-4 w-[100vw] max-w-[100vw] -translate-x-1/2 px-4 sm:mt-8"
                >
                  <motion.div
                    className="mx-auto flex w-full max-w-lg flex-col items-center text-center"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                  >
                    <motion.div
                      className="flex items-center justify-center gap-2 text-lap-gain"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', delay: 0.2 }}
                    >
                      <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5" />
                      <span className="text-xs font-medium sm:text-sm">Signed in successfully</span>
                    </motion.div>
                    <motion.p
                      className="mt-2 text-lg font-light text-white sm:mt-4 sm:text-2xl"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.3 }}
                    >
                      Welcome back, <span className="font-semibold text-lap-accent-bright">{username}</span>
                    </motion.p>
                  </motion.div>
                </div>
              )}
            </AnimatePresence>
          </div>

          {showingMark && from && to && (
            <motion.div
              data-testid="welcome-drop-mark"
              className="fixed z-[101] pointer-events-none origin-top-left text-lap-text will-change-transform"
              style={{ left: from.left, top: from.top, width: from.width, height: from.height }}
              initial={{
                x: 0,
                y: 0,
                scaleX: 1,
                scaleY: 1,
                opacity: 1,
              }}
              animate={
                phase === 'drop'
                  ? {
                      ...markTransform,
                      opacity: 1,
                    }
                  : {
                      ...markTransform,
                      opacity: 0,
                    }
              }
              transition={
                phase === 'drop'
                  ? { duration: DROP_SECONDS, ease: [0.22, 0.08, 0.28, 1] }
                  : { duration: BLEND_SECONDS, ease: 'easeInOut' }
              }
              onAnimationComplete={() =>
                setPhase((p) => (p === 'drop' ? 'blend' : p === 'blend' ? 'logo' : p))
              }
            >
              <LapturaMark className="h-full w-auto" role="img" aria-label="Laptura" />
            </motion.div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
